import React, { useState, useEffect, useCallback } from 'react';
import { FileSignature, X, Loader, Eye, Lock } from 'lucide-react';
import { buildAdvanceAgreementPdfBytes } from '../utils/advanceAgreement';
import { bytesToBase64 } from '../utils/subcontractorAgreement';
import { SignaturePad } from './LeaseSignCard';

// ── Advance agreement e-signature ─────────────────────────────────────────────
// Sibling of ContractorAgreementCard and LeaseSignCard. Shows in the portal when the
// signed-in driver has an advance agreement waiting. The modal rebuilds the exact PDF
// from the terms frozen on the agreement when it was sent, stamps the signature on the
// execution page and submits the signed bytes.
//
// ONE signature covers both roles — he signs for the company AND as guarantor — which
// is why the confirmation below spells out the personal liability rather than burying
// it. Signing a guarantee is the part of this document that can cost him personally, so
// the tick-box has to say so in words he'd actually read.

const ADVANCES_URL = '/.netlify/functions/advances-sb';
const todayLocalISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const money = (n) => `$${(Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function advanceToData(a, extra = {}) {
  const tm = a.terms || {};
  return {
    subcontractor: {
      entity: tm.entity || '',
      abn: tm.abn || '',
      acn: tm.acn || '',
      address: tm.address || '',
      repName: tm.repName || '',
      repPosition: tm.repPosition || 'Director',
    },
    amount: tm.amount || 0,
    instalment: tm.instalment || 0,
    capPct: tm.capPct || 0,
    firstPeriodEndISO: tm.firstPeriodEndISO || '',
    finalDueISO: tm.finalDueISO || '',
    bankLine: tm.bankLine || '',
    purpose: a.purpose || '',
    agreementDate: a.agreementDate || '',
    hprSignature: a.hprSignature || null,
    hprSignatory: a.hprSignatory || '',
    hprPosition: a.hprPosition || '',
    hprSignedDate: a.hprSignedDate || '',
    ...extra,
  };
}

export function AdvanceSignModal({ agreement, onClose, onSigned }) {
  const [previewUrl, setPreviewUrl] = useState('');
  const [signature, setSignature] = useState('');
  const [printName, setPrintName] = useState((agreement.terms && agreement.terms.repName) || '');
  const [agree, setAgree] = useState(false);
  const [guarantee, setGuarantee] = useState(false);
  const [busy, setBusy] = useState(false);
  const tm = agreement.terms || {};
  const entity = tm.entity || agreement.driverLogin;

  // The same signature is applied for the company and for the guarantee.
  const build = (sig) => buildAdvanceAgreementPdfBytes(
    advanceToData(agreement, sig
      ? {
        subSignature: sig, subSignedDate: todayLocalISO(), subPrintName: printName,
        guarantorSignature: sig, guarantorSignedDate: todayLocalISO(), guarantorPrintName: printName,
      }
      : {}),
  );

  useEffect(() => {
    let url = ''; let alive = true;
    (async () => {
      try {
        const bytes = await build(null);
        if (!alive) return;
        url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
        setPreviewUrl(url);
      } catch { setPreviewUrl(''); }
    })();
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [agreement]);

  const submit = async () => {
    if (!signature) return;
    setBusy(true);
    try {
      const bytes = await build(signature);
      const res = await fetch(ADVANCES_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          action: 'signAdvanceAgreement', advanceId: agreement.advanceId,
          signedPdfBase64: bytesToBase64(bytes), signedName: printName,
        }),
      }).then((r) => r.json());
      if (res.success) { if (onSigned) onSigned(agreement.advanceId); onClose(); }
      else alert(`Could not submit: ${res.error || 'unknown error'}`);
    } catch (e) {
      alert(`Error: ${e.message}`);
    } finally { setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1200, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '24px 12px', overflowY: 'auto' }}>
      <div style={{ width: 680, maxWidth: '100%', background: 'var(--hp-card)', color: 'var(--hp-text)', borderRadius: 14, border: '1px solid var(--hp-border)', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid var(--hp-border)' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Advance Agreement</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ padding: 20 }}>
          <p style={{ margin: '0 0 12px', fontSize: '0.84rem', color: 'var(--hp-dim)' }}>
            Please read this agreement for <strong style={{ color: 'var(--hp-text)' }}>{entity}</strong> before signing. This is a legal document and you are encouraged to get independent advice first.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, padding: '12px 14px', borderRadius: 10, background: 'rgba(212,175,55,0.08)', border: '1px solid var(--hp-gold)', marginBottom: 14, fontSize: '0.82rem' }}>
            <div><div style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}>You receive</div><b>{money(tm.amount)}</b></div>
            <div><div style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}>Repaid from each invoice</div><b>{money(tm.instalment)}</b></div>
            <div><div style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}>Interest &amp; fees</div><b>None</b></div>
            <div><div style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}>Repaid in full by</div><b>{tm.finalDueISO || '—'}</b></div>
          </div>

          {previewUrl ? (
            <iframe title="Advance agreement preview" src={previewUrl} style={{ width: '100%', height: 360, border: '1px solid var(--hp-border)', borderRadius: 8, background: '#fff' }} />
          ) : (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--hp-muted)' }}><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Building document…</div>
          )}
          {previewUrl && (
            <a href={previewUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.76rem', color: 'var(--hp-gold)', marginTop: 6 }}>
              <Eye size={12} /> Open full document
            </a>
          )}

          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: 6 }}>Your full name</div>
            <input
              value={printName}
              onChange={(e) => setPrintName(e.target.value)}
              placeholder="Your full name"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-card)', color: 'var(--hp-text)', fontSize: '0.95rem', boxSizing: 'border-box', marginBottom: 10 }}
            />
            <div style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: 6 }}>Your signature</div>
            <SignaturePad onChange={setSignature} defaultName={printName} />
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 14, fontSize: '0.82rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: 3 }} />
            <span>I, {printName || 'the signatory'}, am authorised to sign for {entity}, and I authorise Hollypaul Recruitment to deduct {money(tm.instalment)} from each invoice payment until the {money(tm.amount)} is repaid.</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10, fontSize: '0.82rem', cursor: 'pointer', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--hp-gold)', background: 'rgba(212,175,55,0.06)' }}>
            <input type="checkbox" checked={guarantee} onChange={(e) => setGuarantee(e.target.checked)} style={{ marginTop: 3 }} />
            <span>
              <b>Personal guarantee (clause 10).</b> I understand that I am also signing in my own name, and that if {entity} does not repay this money, Hollypaul Recruitment can recover it from me personally.
            </span>
          </label>
        </div>

        <div style={{ display: 'flex', gap: 10, padding: '16px 20px', borderTop: '1px solid var(--hp-border)' }}>
          <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} disabled={!signature || !agree || !guarantee || !printName.trim() || busy} onClick={submit}>
            {busy ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Submitting…</> : <><Lock size={14} /> Sign &amp; submit</>}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function AdvanceAgreementCard({ shift, onCountChange }) {
  const driverLogin = (shift?.driver || '').toUpperCase();
  const [pending, setPending] = useState([]);
  const [open, setOpen] = useState(null);

  const setPendingSynced = useCallback((next) => {
    setPending((prev) => {
      const val = typeof next === 'function' ? next(prev) : next;
      if (onCountChange) onCountChange(val.length);
      return val;
    });
  }, [onCountChange]);

  const load = useCallback(async () => {
    try {
      const d = await fetch(ADVANCES_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', body: JSON.stringify({ action: 'getMyAdvanceAgreements' }),
      }).then((r) => r.json());
      if (d.success) setPendingSynced(d.agreements || []);
    } catch { /* silent — the portal shouldn't break on this */ }
  }, [setPendingSynced]);

  useEffect(() => { if (driverLogin) load(); }, [driverLogin, load]);

  const decline = async (a) => {
    const reason = window.prompt(`Decline the advance agreement for ${(a.terms && a.terms.entity) || a.driverLogin}? Optionally add a reason:`, '');
    if (reason === null) return;
    try {
      const res = await fetch(ADVANCES_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin',
        body: JSON.stringify({ action: 'declineAdvanceAgreement', advanceId: a.advanceId, reason }),
      }).then((r) => r.json());
      if (res.success) setPendingSynced((prev) => prev.filter((x) => x.advanceId !== a.advanceId));
      else alert(`Could not decline: ${res.error || 'unknown error'}`);
    } catch (e) { alert(`Error: ${e.message}`); }
  };

  if (!pending.length) return null;

  return (
    <div style={{ width: '100%', marginBottom: 16 }}>
      {pending.map((a) => (
        <div key={a.advanceId} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, padding: '14px 16px', borderRadius: 12, background: 'rgba(212,175,55,0.08)', border: '1px solid var(--hp-gold)', marginBottom: 10 }}>
          <FileSignature size={22} color="var(--hp-gold)" style={{ flexShrink: 0 }} />
          <div style={{ flex: '1 1 190px', minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>Advance agreement to sign</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
              {(a.terms && a.terms.entity) || a.driverLogin}
              {a.terms && a.terms.amount ? ` · ${money(a.terms.amount)} advance` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 'auto' }}>
            <button className="btn btn-primary btn-sm" onClick={() => setOpen(a)}>Review &amp; sign</button>
            <button className="btn btn-ghost btn-sm" onClick={() => decline(a)}>Decline</button>
          </div>
        </div>
      ))}

      {open && (
        <AdvanceSignModal
          agreement={open}
          onClose={() => setOpen(null)}
          onSigned={(advanceId) => setPendingSynced((prev) => prev.filter((a) => a.advanceId !== advanceId))}
        />
      )}
    </div>
  );
}
